/**
 * §A.2.1 · Los clubes a los que hay que llamar.
 *
 * Dos llamadas, y ninguna la hace el sistema por nosotros:
 *
 * - **«Tenéis que poneros al día.»** El pago venció. Primero los que ya tienen el
 *   panel en solo lectura; luego los que están en su gracia, que todavía no han
 *   notado nada.
 * - **«Tenéis que subir de plan.»** Se pasan de alumnos o de sedes. **No se les ha
 *   cortado nada** —los límites son blandos a propósito— y por eso, si esta lista no
 *   la mira nadie, nadie se entera.
 *
 * Cada mañana `platform.plan_watch` manda un correo la primera vez; esta pantalla
 * enseña la lista entera siempre. El orden lo pone el servidor: aquí no se calcula
 * nada, para que la pantalla y el correo no discrepen.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fecha } from '@/shared/ui/formato';
import { SITUACIONES_DEL_PAGO, type AvisoDePlan } from '@/backoffice/tipos';

const uso = (
  u: { used: number; max: number | null },
  una: string,
  varias: string
) => `${u.used} de ${u.max ?? '∞'} ${u.max === 1 ? una : varias}`;

const seSale = (u: { used: number; max: number | null }) =>
  u.max !== null && u.used > u.max;

export function AvisosDePlanes({
  alAbrirClub,
}: {
  alAbrirClub: (id: string) => void;
}) {
  const avisos = useQuery({
    queryKey: ['admin', 'avisos-de-planes'],
    queryFn: async () => {
      const { data, error } = await api.v1.admin['plan-alerts'].get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as AvisoDePlan[];
    },
  });

  if (avisos.isPending) {
    return <Cargando filas={4} etiqueta="Cargando los avisos" />;
  }

  if (avisos.isError) {
    return (
      <PanelError
        problema={aProblema(avisos.error)}
        reintentar={() => void avisos.refetch()}
      />
    );
  }

  if (avisos.data.length === 0) {
    return (
      <Vacio
        titulo="Nadie a quien llamar"
        explicacion="Todos los clubes con plan están al día y dentro de sus límites."
      />
    );
  }

  return (
    <section>
      <p className="tenue">
        Los límites avisan y no cortan: un club que se pasa sigue funcionando.
        El pago tiene sus días de gracia, y después el panel del club pasa a
        solo lectura —la app del maestro y la de familias siguen—.
      </p>

      <div className="tabla-marco">
        <table className="tabla">
          <thead>
            <tr>
              <th>Club</th>
              <th>Plan</th>
              <th>Pago</th>
              <th>Alumnos</th>
              <th>Sedes</th>
            </tr>
          </thead>
          <tbody>
            {avisos.data.map((a) => {
              const situacion = SITUACIONES_DEL_PAGO[a.standing];
              return (
                <tr key={a.academy_id}>
                  <td>
                    <button
                      type="button"
                      className="enlace-fila"
                      onClick={() => alAbrirClub(a.academy_id)}
                    >
                      {a.trade_name}
                    </button>
                  </td>
                  <td className="tenue">{a.plan_name}</td>
                  <td>
                    {a.standing === 'current' ? (
                      <span className="tenue">Al día</span>
                    ) : (
                      <>
                        <span className={`pastilla ${situacion.clase}`}>
                          {situacion.etiqueta}
                        </span>{' '}
                        <span className="tenue">
                          {a.standing === 'due'
                            ? `cubierto hasta el ${fecha(a.paid_through)}, gracia hasta el ${fecha(a.grace_ends_on)}`
                            : `la gracia acabó el ${fecha(a.grace_ends_on)}`}
                        </span>
                      </>
                    )}
                  </td>
                  <td className={seSale(a.usage.students) ? 'texto-mal' : ''}>
                    {uso(a.usage.students, 'alumno', 'alumnos')}
                  </td>
                  <td className={seSale(a.usage.venues) ? 'texto-mal' : ''}>
                    {uso(a.usage.venues, 'sede', 'sedes')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
